import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { useLang } from "@/i18n";
import { BlinkingEyes } from "@/components/BlinkingEyes";
import { PackSwap } from "@/components/BagPack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NOTE: stock mountain image — pending license verification before production.
// Do NOT replace with an AI-generated image. Mark for later swap if unverifiable.
const HERO_IMG = "https://images.pexels.com/photos/6877843/pexels-photo-6877843.jpeg";
const heroSrc = (w) => `${HERO_IMG}?auto=compress&cs=tinysrgb&w=${w}`;
const HERO_SRCSET = [640, 960, 1280, 1600, 1920].map((w) => `${heroSrc(w)} ${w}w`).join(", ");
const ROTATE_MS = 4000;

const Hero = () => {
  const { lang, t } = useLang();
  const reduce = useReducedMotion();
  const [bags, setBags] = useState([]);
  const [idx, setIdx] = useState(0);
  const [slots, setSlots] = useState([0, null]);
  const [active, setActive] = useState(0);
  const [phase, setPhase] = useState("idle");
  const ref = useRef({ slots: [0, null], active: 0 });
  ref.current = { slots, active };

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setBags(res.data))
      .catch((e) => console.error("hero products error", e));
  }, []);

  useEffect(() => {
    if (bags.length < 2) return undefined;
    const id = setTimeout(() => setIdx((i) => (i + 1) % bags.length), ROTATE_MS);
    return () => clearTimeout(id);
  }, [bags.length, idx]);

  // Sticker swap, double buffered: the new label is written into the hidden slot
  // (already rasterised), then the visible slot fades out 150 ms and the new one
  // fades in 150 ms. The bag itself and its float are never touched.
  useEffect(() => {
    if (bags.length === 0) return undefined;
    const cur = ref.current.active;
    if (ref.current.slots[cur] === idx) return undefined;
    const other = 1 - cur;
    setSlots((s) => (other === 0 ? [idx, s[1]] : [s[0], idx]));
    setPhase("out");
    const t1 = setTimeout(() => {
      setActive(other);
      setPhase("in");
    }, 160);
    // once the swap is over the hidden slot is emptied: only the label actually
    // shown stays in the DOM (no other coffee's data lingering behind it)
    const t2 = setTimeout(() => {
      setPhase("idle");
      setSlots((s) => (cur === 0 ? [null, s[1]] : [s[0], null]));
    }, 330);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [idx, bags.length]);

  const packSlots = slots.map((i) => (i === null ? null : bags[i] || null));
  const fading = phase === "out";
  const shown = slots[active] ?? 0;
  const activeProduct = bags[shown];
  // Capsule B only ever shows a VERIFIED field: nothing is invented and no
  // default is borrowed from another origin. Coffees without an approved spec
  // sheet simply have no lower capsule at all.
  const capKeys = ["altitud", "clasificacion", "proceso", "variedad"];
  const capKeyOf = (p) => (p ? capKeys.find((k) => p.specs?.[k]?.[lang]) : null);
  const capKey = capKeyOf(activeProduct);
  const capMounted = Boolean(capKey || capKeyOf(bags[idx]));
  // "Naguarà · Nicaragua Descafeinado" -> "Nicaragua Descafeinado"
  const shortName = activeProduct ? activeProduct.name.replace(/^Naguarà\s·\s/, "") : "";

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const rise = (delay) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.4, delay } }
      : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay } };

  return (
    <section
      data-testid="hero-section"
      className="relative flex items-center overflow-hidden min-h-[620px] md:min-h-[760px] md:max-h-[900px] md:h-[86vh]"
    >
      {/* Mountains with slow breathing zoom */}
      <div className="absolute inset-0 z-0 hero-photo">
        <img
          src={heroSrc(1920)}
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt="Montañas de cafetales al amanecer, el paisaje del origen de Naguarà"
          data-license="pending-verification"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Legibility gradients (dark left) */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#2A1B18] via-[#2A1B18]/85 to-[#2A1B18]/25" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#2A1B18]/70 via-transparent to-[#2A1B18]/40" />

      {/* Fine drifting mist (center/right only) */}
      <div
        className="hero-mist-1 absolute inset-y-0 right-0 w-[75%] z-[2] pointer-events-none"
        style={{ background: "radial-gradient(55% 45% at 62% 42%, rgba(245,241,232,0.14), rgba(245,241,232,0) 70%)", filter: "blur(34px)" }}
      />
      <div
        className="hero-mist-2 absolute inset-y-0 right-0 w-[70%] z-[2] pointer-events-none"
        style={{ background: "radial-gradient(50% 40% at 72% 60%, rgba(245,241,232,0.10), rgba(245,241,232,0) 72%)", filter: "blur(40px)" }}
      />

      {/* Right floating composition — real pack carousel (desktop / tablet).
          Capsules sit above/below the pack so the sticker is never covered. */}
      {bags.length > 0 && (
        <div className="hidden md:flex absolute inset-y-0 right-[44px] lg:right-[90px] z-[5] items-center pointer-events-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            data-testid="hero-bag-carousel"
            className="flex flex-col items-center"
          >
            {/* Fixed capsule boxes: only the text opacity changes */}
            <div className="self-start mb-5 w-[254px] h-[34px] flex items-center justify-center bg-[#F5F1E8]/85 backdrop-blur-sm rounded-full border border-[#3B2523]/10 shadow-lg">
              <span
                data-testid="hero-cap-origin"
                className={`text-[#3B2523] text-xs font-semibold whitespace-nowrap transition-opacity duration-150 ease-linear ${
                  fading || !activeProduct ? "opacity-0" : "opacity-100"
                }`}
              >
                {activeProduct
                  ? `${activeProduct.origin}${activeProduct.region ? ` · ${activeProduct.region[lang]}` : ""}`
                  : ""}
              </span>
            </div>

            <div className="w-[262px] h-[268px] lg:h-[307px] flex items-center justify-center">
              <PackSwap
                slots={packSlots}
                active={active}
                phase={phase}
                priority
                testId="hero-pack"
                stageClass="w-[213px] h-[260px] lg:w-[245px] lg:h-[299px]"
                shadow="drop-shadow-[0_16px_26px_rgba(0,0,0,0.38)]"
                href={activeProduct ? `/cafe/${activeProduct.id}` : undefined}
                ariaLabel={activeProduct ? `${t.producto.viewLabel} ${shortName}` : undefined}
              />
            </div>

            {/* Lower capsule: absolutely positioned, so it reserves NO space and the
                indicators never move. It only exists when the coffee (or the incoming
                one, to keep the 150 ms fade) has a verified field. */}
            <div className="relative w-[254px] h-0">
              {capMounted ? (
                <div
                  className={`absolute right-0 top-5 w-[246px] h-[34px] flex items-center justify-center bg-[#F5F1E8]/85 backdrop-blur-sm rounded-full border border-[#3B2523]/10 shadow-lg transition-opacity duration-150 ease-linear ${
                    fading || !capKey ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <span
                    data-testid="hero-cap-altitude"
                    className="text-[#3B2523] text-xs font-semibold whitespace-nowrap"
                  >
                    {capKey ? `${t.encuentra.specs[capKey]} · ${activeProduct.specs[capKey][lang]}` : ""}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-2.5 mt-[78px] pointer-events-auto">
              {bags.map((b, i) => (
                <button
                  key={b.id}
                  type="button"
                  aria-label={b.origin}
                  data-testid={`hero-bag-dot-${b.id}`}
                  onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    i === idx ? "w-7 bg-[#F4D35E]" : "w-2 bg-[#F5F1E8]/40 hover:bg-[#F5F1E8]/70"
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 px-6 md:px-12 lg:px-20 w-full pt-28 pb-20 pointer-events-none">
        <div className="max-w-[680px] md:max-w-[440px] lg:max-w-[600px] xl:max-w-[680px] pointer-events-auto flex flex-col">
          <motion.div {...rise(0.05)} className="flex items-center gap-3 mb-4">
            <BlinkingEyes variant="beige" width={46} testId="hero-mascot-eyes" />
            <span className="text-[#F4D35E] uppercase tracking-[0.28em] text-xs md:text-sm font-semibold">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <motion.h1
            {...rise(0.18)}
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 }}
            className="text-[#F5F1E8] tracking-tight leading-[1.05] text-[38px] sm:text-[48px] lg:text-[62px] xl:text-[72px]"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            {...rise(0.3)}
            className="text-[#E6D9B6] text-base md:text-lg mt-6 leading-relaxed max-w-[620px]"
          >
            {t.hero.text}
          </motion.p>

          <motion.p
            {...rise(0.38)}
            className="text-[#F5F1E8]/80 text-sm md:text-base mt-5 italic leading-relaxed max-w-[560px]"
          >
            {t.hero.support}
          </motion.p>

          <motion.div
            {...rise(0.48)}
            className="order-6 md:order-none flex flex-col sm:flex-row gap-3 sm:gap-4 mt-[22px] md:mt-9 w-full sm:w-auto"
          >
            <button
              data-testid="hero-shop-btn"
              onClick={() => scrollTo("coffees")}
              className="w-full sm:w-auto min-h-[48px] bg-[#F4D35E] text-[#3B2523] font-semibold px-8 py-4 rounded-full transition-transform duration-300 hover:-translate-y-1"
            >
              {t.hero.cta1}
            </button>
            <button
              data-testid="hero-horeca-btn"
              onClick={() => scrollTo("hosteleria")}
              className="w-full sm:w-auto min-h-[48px] border border-[#E6D9B6]/60 text-[#E6D9B6] font-semibold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#E6D9B6] hover:text-[#3B2523]"
            >
              {t.hero.cta2}
            </button>
          </motion.div>

          {/* Mobile (<768 px): pack + indicators go BEFORE the buttons, so the
              CTAs sit right under the bag, fully visible and never on top of it. */}
          {bags.length > 0 && (
            <div
              className="md:hidden order-5 mt-10 flex flex-col items-center"
              data-testid="hero-bag-carousel-mobile"
            >
              <div className="mb-3 h-[16px] flex items-center justify-center">
                <span
                  data-testid="hero-cap-origin-mobile"
                  className={`text-[#F5F1E8]/85 text-xs uppercase tracking-[0.24em] font-semibold whitespace-nowrap transition-opacity duration-150 ease-linear ${
                    fading || !activeProduct ? "opacity-0" : "opacity-100"
                  }`}
                >
                  {activeProduct ? activeProduct.origin : ""}
                </span>
              </div>

              <div className="w-[198px] h-[240px] flex items-center justify-center">
                <PackSwap
                  slots={packSlots}
                  active={active}
                  phase={phase}
                  priority
                  testId="hero-pack-mobile"
                  stageClass="w-[190px] h-[232px]"
                  shadow="drop-shadow-[0_14px_22px_rgba(0,0,0,0.42)]"
                  href={activeProduct ? `/cafe/${activeProduct.id}` : undefined}
                  ariaLabel={activeProduct ? `${t.producto.viewLabel} ${shortName}` : undefined}
                />
              </div>

              {/* 12 px under the bag. Anchored: they never float with it. */}
              <div className="flex items-center gap-1 mt-3" data-testid="hero-bag-dots-mobile">
                {bags.map((b, i) => (
                  <button
                    key={b.id}
                    type="button"
                    aria-label={b.origin}
                    aria-current={i === idx}
                    data-testid={`hero-bag-dot-mobile-${b.id}`}
                    onClick={() => setIdx(i)}
                    className="h-11 px-2 flex items-center justify-center"
                  >
                    <span
                      className={`block h-2 rounded-full transition-all duration-500 ${
                        i === idx ? "w-7 bg-[#F4D35E]" : "w-2 bg-[#F5F1E8]/40"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
